'use client';

import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import AllFiltersModal from './AllFiltersModal';
import { type FilterTrackingContext, SRP_FILTER_TRACKING } from './filterAnalytics';
import type { AreaResponse, AutocompleteResult } from '@/src/lib/api';
import type { FilterState } from '@/src/lib/filters/filterTypes';
import { type ListingSearchMode, useListingSearchState } from './useListingSearchState';
import { defaultModalOptions, type OpenAllFiltersOptions, useAllFiltersModal } from './AllFiltersModalProvider';

type SearchFiltersContextValue = {
  filters: FilterState;
  setFilters: (next: FilterState) => void;
  applyFilters: (patch: Partial<FilterState>, options?: { immediate?: boolean }) => void;
  area: AreaResponse | AutocompleteResult | null;
  setArea: (area: AutocompleteResult) => void;
  submit: () => void;
  searchArea: (area: AutocompleteResult) => void;
  isPending: boolean;
  activeFilterCount: number;
  nearMeArea?: AreaResponse | null;
  openAllFilters: (options: OpenAllFiltersOptions) => void;
  closeAllFilters: () => void;
  markUserInteraction: () => void;
};

type SearchFiltersProviderProps = {
  children: ReactNode;
  mode: ListingSearchMode;
  trackingContext?: FilterTrackingContext | null;
  fallbackArea?: AreaResponse | null;
  nearMeArea?: AreaResponse | null;
  seedFromRecentSearch?: boolean;
  defaultFilters?: FilterState;
};

const SearchFiltersContext = createContext<SearchFiltersContextValue | null>(null);

export function SearchFiltersProvider({
  children,
  mode,
  fallbackArea,
  nearMeArea,
  seedFromRecentSearch,
  defaultFilters,
  trackingContext = SRP_FILTER_TRACKING,
}: SearchFiltersProviderProps) {
  const {
    filters,
    setFilters,
    applyFilters,
    area,
    setArea,
    submit,
    searchArea,
    isPending,
    activeFilterCount,
    nearMeArea: resolvedNearMeArea,
    markUserInteraction,
  } = useListingSearchState({
    mode,
    fallbackArea,
    nearMeArea,
    seedFromRecentSearch,
    trackingContext,
    defaultFilters,
  });

  // The All-Filters modal is hosted by the persistent search shell when present
  // (SRP/HDP) so it survives the slug morph that remounts the page-level search bar.
  // Without a shell (e.g. the homepage hero), fall back to a local modal.
  const shellModal = useAllFiltersModal();
  const [localOpen, setLocalOpen] = useState(false);
  const [localHasOpened, setLocalHasOpened] = useState(false);
  const [localOptions, setLocalOptions] = useState<OpenAllFiltersOptions>(defaultModalOptions);

  const openAllFilters = useCallback(
    (options: OpenAllFiltersOptions) => {
      markUserInteraction();
      if (shellModal) {
        shellModal.openAllFilters({ ...options, trackingContext });
        return;
      }
      setLocalOptions(options);
      setLocalHasOpened(true);
      setLocalOpen(true);
    },
    [markUserInteraction, shellModal, trackingContext],
  );

  const closeAllFilters = useCallback(() => {
    if (shellModal) {
      shellModal.closeAllFilters();
      return;
    }
    setLocalOpen(false);
  }, [shellModal]);

  const value = useMemo(
    () => ({
      filters,
      setFilters,
      applyFilters,
      area,
      setArea,
      submit,
      searchArea,
      isPending,
      activeFilterCount,
      nearMeArea: resolvedNearMeArea,
      openAllFilters,
      closeAllFilters,
      markUserInteraction,
    }),
    [
      filters,
      setFilters,
      applyFilters,
      area,
      setArea,
      submit,
      searchArea,
      isPending,
      activeFilterCount,
      resolvedNearMeArea,
      openAllFilters,
      closeAllFilters,
      markUserInteraction,
    ],
  );

  return (
    <SearchFiltersContext.Provider value={value}>
      {children}
      {!shellModal && localHasOpened && (
        <AllFiltersModal
          trackingContext={trackingContext}
          isOpen={localOpen}
          onClose={closeAllFilters}
          filters={filters}
          setFilters={setFilters}
          area={area}
          showRoomsSection={localOptions.showRoomsSection}
          showPriceSection={localOptions.showPriceSection}
        />
      )}
    </SearchFiltersContext.Provider>
  );
}

export function useSearchFilters(): SearchFiltersContextValue {
  const context = useContext(SearchFiltersContext);

  if (!context) {
    throw new Error('useSearchFilters must be used within a SearchFiltersProvider');
  }

  return context;
}

export type { OpenAllFiltersOptions } from './AllFiltersModalProvider';
